/**
 * The company STAGE STATUS master (`/company-stage-statuses`, owner 2026-10-23)
 * for pickers: the "Stage status" options under each company stage.
 *
 * One request is shared by every caller on a page (a list renders a picker per
 * row), cached briefly so a Catalog edit shows up without a reload. A failed
 * read is not cached.
 *
 * ⚠️ **A picker offers only the ACTIVE statuses of the company's CURRENT (or
 * just-picked) stage.** A retired status the company still holds is kept by
 * passing its id as `includeInactiveId`, so the stored value still displays.
 * Never match a status by name: names repeat across stages.
 */

import { useCallback, useEffect, useState } from 'react';
import { api } from './api';
import type { CompanyStageStatus } from './types';

const CACHE_MS = 60_000;

let cached: { at: number; promise: Promise<CompanyStageStatus[]> } | null = null;

function loadStatuses(force = false): Promise<CompanyStageStatus[]> {
  if (!force && cached && Date.now() - cached.at < CACHE_MS) return cached.promise;
  const promise = api.listCompanyStageStatuses().then((result) => result.data);
  const entry = { at: Date.now(), promise };
  cached = entry;
  promise.catch(() => {
    if (cached === entry) cached = null;
  });
  return promise;
}

/** Drop the shared cache, so the next read refetches. Call after a Catalog write. */
export function invalidateCompanyStageStatuses(): void {
  cached = null;
}

export interface StatusesForStageOptions {
  /**
   * The status the record already holds. Kept in the result even when it is
   * retired, so the stored value still displays. Ignored when it belongs to a
   * different stage (a stage change clears the status).
   */
  includeInactiveId?: number | null;
}

/**
 * The statuses of one stage, in the master's own order: ACTIVE rows, plus the
 * `includeInactiveId` row when it is retired. `stageId` null/undefined → `[]`
 * (a company with no stage cannot hold a status).
 */
export function filterStatusesForStage(
  statuses: readonly CompanyStageStatus[],
  stageId: number | null | undefined,
  options: StatusesForStageOptions = {},
): CompanyStageStatus[] {
  if (stageId === null || stageId === undefined) return [];
  const keep = options.includeInactiveId ?? null;
  return statuses.filter(
    (status) => status.stage.id === stageId && (status.isActive || status.id === keep),
  );
}

export interface CompanyStageStatusesState {
  /** Every status, active and retired, in the server's order. */
  statuses: CompanyStageStatus[];
  loading: boolean;
  /** The read failed. `statuses` is then `[]`. */
  error: string | null;
  /** {@link filterStatusesForStage} bound to `statuses`. */
  statusesForStage: (
    stageId: number | null | undefined,
    options?: StatusesForStageOptions,
  ) => CompanyStageStatus[];
  /** Refetch, bypassing the cache. */
  reload: () => void;
}

export function useCompanyStageStatuses(enabled = true): CompanyStageStatusesState {
  const [statuses, setStatuses] = useState<CompanyStageStatus[]>([]);
  const [loading, setLoading] = useState(enabled);
  const [error, setError] = useState<string | null>(null);
  const [nonce, setNonce] = useState(0);

  useEffect(() => {
    if (!enabled) return;
    let alive = true;
    loadStatuses(nonce > 0)
      .then((rows) => {
        if (!alive) return;
        setStatuses(rows);
        setError(null);
      })
      .catch((caught: unknown) => {
        if (!alive) return;
        setStatuses([]);
        setError(caught instanceof Error ? caught.message : 'Failed to load stage statuses');
      })
      .finally(() => {
        if (alive) setLoading(false);
      });
    return () => {
      alive = false;
    };
  }, [enabled, nonce]);

  const statusesForStage = useCallback(
    (stageId: number | null | undefined, options?: StatusesForStageOptions) =>
      filterStatusesForStage(statuses, stageId, options),
    [statuses],
  );

  const reload = useCallback(() => setNonce((value) => value + 1), []);

  return {
    statuses,
    loading: enabled ? loading : false,
    error,
    statusesForStage,
    reload,
  };
}
